import React, { Component } from 'react';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { getCompanyID } from 'helper/user';
import { getSelectedShopifyShop } from 'helper/shopify';
import {
  trackPageView,
  event,
  cat,
  custom,
} from 'common/utils/instrumentation';
import { isPaxUser } from 'helper/grab-id-auth-helper';

import {
  bookAgainAction,
  updateOrderShallowAction,
} from 'actions/booking-actions';
import {
  fetchOngoingDeliveriesIntervalAction,
  paxFetchOngoingDeliveriesIntervalAction,
  fetchCancelReasonsAction,
  fetchCompletedDeliveriesAction,
  cancelDeliveryActionV2,
  stopFetchOngoingDeliveriesIntervalAction,
  stopPaxFetchOngoingDeliveriesIntervalAction,
  paxCancelDeliveryAction,
  paxFetchCompletedDeliveriesAction,
  submitCancelReasonsAction,
  submitCancelReasonsForMultipleOrdersAction,
} from 'actions/history-actions';
import { shopifyBookAgainAction } from 'actions/shopify-actions';
import {
  isRequiredOrderIDSelector,
  userInfoSelector,
  enableWebCancellationSelector,
} from 'selectors/app-selector';
import { shopifyShopsSelector } from 'selectors/shopify-selectors';
import { fetchPreviousBookings } from 'helper/history-order';
import { GFB_ORDER_HISTORY_FETCH_DAYS } from 'constants/history-constants.js';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BOOKING_EVENTS,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import ScribeDuck from 'ducks/scribe-duck';
import NewHistoryDuck from 'ducks/new-history-duck';

import OrderHistoryPage from './order-history';
import GrabIDOrderHistoryPage from './grab-id-order-history';

class OrderHistory extends Component {
  componentDidMount() {
    const {
      userInfo,
      fetchAllPreviousBookings,
      fetchOngoingDeliveriesInterval,
      fetchCancelReasons,
      location,
      countingOngoingOrders,
    } = this.props;
    const companyID = getCompanyID(userInfo);

    trackPageView(event.BOOKINGS_PV, cat.GE_BOOKINGS, {
      [custom.COMPANY_ID]: companyID,
      ONGOING_BOOKINGS: countingOngoingOrders.total,
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.DEFAULT,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.ONGOING_BOOKINGS]: countingOngoingOrders.total,
      },
    });

    const fetchParams = isPaxUser()
      ? {
          fetchPreviousBookingsAction: fetchAllPreviousBookings,
          // 0,0 means fetch all recent orders, up to 40
          startTimestamp: 0,
          endTimestamp: 0,
        }
      : {
          fetchPreviousBookingsAction: fetchAllPreviousBookings,
          fetchDays: GFB_ORDER_HISTORY_FETCH_DAYS,
          fetchLimit: -1, // -1 Means Fetch All for GFB user endpoint
          companyID,
        };

    fetchPreviousBookings(fetchParams);
    fetchOngoingDeliveriesInterval({ companyID });
    fetchCancelReasons();

    // reset location state from Home page
    if (location && !_isEmpty(location.state)) {
      window.history.replaceState(null, '');
    }
  }

  componentWillUnmount() {
    const { stopFetchOngoingDeliveriesInterval } = this.props;
    stopFetchOngoingDeliveriesInterval();
  }

  fireShopifyTrackEvent = ({ stateName, eventName, facets = {} }) => {
    const { userInfo, shopifyShops } = this.props;

    const shop = getSelectedShopifyShop(shopifyShops);

    const shopifyCommonFacets = {
      ...getCommonFacet(userInfo),
    };

    if (shop) {
      shopifyCommonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_NAME] = shop.name;
      shopifyCommonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID] = shop.shop;
    }

    scribeTrackEvent({
      stateName,
      eventName,
      facets: {
        ...shopifyCommonFacets,
        ...facets,
      },
    });
  };

  render() {
    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <ShopifyTrackEventContext.Provider
        value={{ fireShopifyTrackEvent: this.fireShopifyTrackEvent }}
      >
        {isPaxUser() ? (
          <GrabIDOrderHistoryPage {...this.props} />
        ) : (
          <OrderHistoryPage {...this.props} />
        )}
      </ShopifyTrackEventContext.Provider>
    );
  }
}

OrderHistory.propTypes = {
  isOngoingOrdersLoading: PropTypes.bool.isRequired,
  isPreviousBookingsLoading: PropTypes.bool.isRequired,
  userInfo: PropTypes.shape({}).isRequired,
  fetchCancelReasons: PropTypes.func.isRequired,
  fetchAllPreviousBookings: PropTypes.func.isRequired,
  fetchOngoingDeliveriesInterval: PropTypes.func.isRequired,
  stopFetchOngoingDeliveriesInterval: PropTypes.func.isRequired,
  onClickPrintLabel: PropTypes.func.isRequired,
  location: PropTypes.shape({}).isRequired,
  countingOngoingOrders: PropTypes.shape({}),
  shopifyShops: PropTypes.arrayOf(PropTypes.shape({})),
  enableWebCancellation: PropTypes.bool.isRequired,
};

OrderHistory.defaultProps = {
  countingOngoingOrders: {},
  shopifyShops: [],
  enableWebCancellation: false,
};

const mapStateToProps = state => ({
  isRequiredOrderID: isRequiredOrderIDSelector(state),
  isOngoingOrdersLoading: state.history.isOngoingOrdersLoading,
  isPreviousBookingsLoading: state.history.isCompletedOrdersLoading,
  userInfo: userInfoSelector(state),
  ongoingOrders: state.history.ongoingOrders,
  ongoingOrdersLastUpdate: state.history.ongoingOrdersLastUpdate,
  completedOrders: state.history.completedOrders,
  completedOrdersSourceOptions: state.history.completedOrdersSourceOptions,
  cancelReasons: state.history.cancelReasons,
  countingOngoingOrders: state.history.countingOngoingOrders,
  shopifyShops: shopifyShopsSelector(state),
  enableWebCancellation: enableWebCancellationSelector(state),
});

// Do not change to the shorthand object return
// Using function form so that this runs when the component is instantiated
const mapDispatchToProps = dispatch => ({
  bookAgain: args => dispatch(bookAgainAction(args)),
  cancelOrder: args => {
    const cancelAction = isPaxUser()
      ? paxCancelDeliveryAction
      : cancelDeliveryActionV2;
    return dispatch(cancelAction(args));
  },
  updateOrderShallow: args => dispatch(updateOrderShallowAction(args)),
  fetchCancelReasons: args => dispatch(fetchCancelReasonsAction(args)),
  fetchAllPreviousBookings: args => {
    const fetchAction = isPaxUser()
      ? paxFetchCompletedDeliveriesAction
      : fetchCompletedDeliveriesAction;
    return dispatch(fetchAction(args));
  },
  fetchOngoingDeliveriesInterval: args => {
    const fetchAction = isPaxUser()
      ? paxFetchOngoingDeliveriesIntervalAction
      : fetchOngoingDeliveriesIntervalAction;
    return dispatch(fetchAction(args));
  },
  stopFetchOngoingDeliveriesInterval: args => {
    const stopAction = isPaxUser()
      ? stopPaxFetchOngoingDeliveriesIntervalAction
      : stopFetchOngoingDeliveriesIntervalAction;
    return dispatch(stopAction(args));
  },
  listCancellationReasons: deliveries =>
    dispatch(NewHistoryDuck.creators.listCancellationReasons(deliveries)),
  submitCancelReason: args => dispatch(submitCancelReasonsAction(args)),
  submitCancelReasonsForMultipleOrders: multipleCancelReq =>
    dispatch(submitCancelReasonsForMultipleOrdersAction(multipleCancelReq)),
  shopifyBookAgain: args => dispatch(shopifyBookAgainAction(args)),
  onClickPrintLabel: () =>
    dispatch(ScribeDuck.creators.scribeExpressBookingsWebPrintLabel()),
  onClickBulkPrintLabel: orderCount =>
    dispatch(
      ScribeDuck.creators.scribeExpressBookingsWebBulkPrintLabel(orderCount),
    ),
  scribeExpressBookingsWebShowOngoingBookings: selectedPageSize =>
    dispatch(
      ScribeDuck.creators.scribeExpressBookingsWebShowOngoingBookings(
        selectedPageSize,
      ),
    ),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OrderHistory),
);
